const imagesCache = new WeakMap();

const ProductBlockByColors = {
  getImages: function (productBlock) {
    if (imagesCache.has(productBlock)) return imagesCache.get(productBlock);

    const data = productBlock.querySelector('[data-product-images-json]');
    if (!data) return [];

    try {
      const images = JSON.parse(data.textContent);
      imagesCache.set(productBlock, images);
      return images;
    } catch (error) {
      console.error('ProductBlockByColors: imagens invalidas.', error);
      return [];
    }
  },

  updateImages: function (productBlock, sku) {
    const allImages = ProductBlockByColors.getImages(productBlock);
    if (!allImages.length || !sku) return;

    const skuImages = allImages.filter((image) => String(image.sku) === String(sku));
    if (!skuImages.length) return;

    const figure = productBlock.querySelector('.images figure');
    if (!figure) return;

    const limit = Number(productBlock.dataset.imageLimit);
    if (!Number.isFinite(limit) || limit <= 0) return;

    const currentImages = [...figure.querySelectorAll('[data-product-image]')];
    const alt = currentImages[0]?.getAttribute('alt') ?? '';
    const nextImages = skuImages.slice(0, limit);

    nextImages.forEach((image, index) => {
      const element = currentImages[index] ?? document.createElement('img');

      element.classList.remove('lazy', 'loaded');
      element.removeAttribute('data-src');
      element.removeAttribute('data-srcset');
      element.setAttribute('data-product-image', '');
      element.setAttribute('alt', alt);
      element.setAttribute('sizes', image.sizes);
      element.setAttribute('src', image.src);
      element.setAttribute('srcset', image.srcset);

      if (!currentImages[index]) figure.appendChild(element);
    });

    currentImages.slice(nextImages.length).forEach((image) => image.remove());
  },

  updateLinks: function (productBlock, colorName) {
    const baseUrl = productBlock.dataset.productUrl;
    if (!baseUrl || !colorName) return;

    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('colorName', colorName);
    const nextUrl = `${url.pathname}${url.search}${url.hash}`;

    productBlock.querySelectorAll('[data-product-link]').forEach((link) => {
      link.setAttribute('href', nextUrl);
    });
  },

  setActiveOption: function (productBlock, selector, activeOption) {
    productBlock.querySelectorAll(selector).forEach((option) => {
      const active = option === activeOption;
      option.classList.toggle('-active', active);
      option.setAttribute('aria-pressed', String(active));
    });
  },

  syncFormOption: function (target, name, value) {
    if (!target || !name || !value) return;

    const option = [...target.querySelectorAll('[data-product-purchase] [data-attribute-name]')]
      .find((element) => element.dataset.attributeName?.trim().toLowerCase() === name.trim().toLowerCase());
    if (!option) return;

    const input = [...option.querySelectorAll('input[type="radio"]')]
      .find((element) => element.value === value);
    if (!input || input.checked) return;

    input.click();
  },

  syncSelection: function (source, target = source) {
    if (!source || !target) return;

    ProductBlockByColors.syncFormOption(
      target,
      source.dataset.activeColorName,
      source.dataset.activeColorValue
    );
    ProductBlockByColors.syncFormOption(
      target,
      source.dataset.activeSizeName,
      source.dataset.activeSizeValue
    );
  },

  syncWhenReady: function (source, target = source) {
    const form = target?.querySelector('[data-product-purchase]');
    if (!form) return;

    if (form.getAttribute('data-processed') === 'true') {
      ProductBlockByColors.syncSelection(source, target);
      return;
    }

    const observer = new MutationObserver(() => {
      if (form.getAttribute('data-processed') !== 'true') return;

      observer.disconnect();
      ProductBlockByColors.syncSelection(source, target);
    });

    observer.observe(form, { attributes: true, attributeFilter: ['data-processed'] });
  },

  handleColor: function (option) {
    const productBlock = option.closest('.product-block');
    if (!productBlock) return;

    const sku = option.dataset.colorSku;
    const colorName = option.dataset.colorName ?? '';
    const optionName = option.dataset.optionName ?? '';
    const optionValue = option.dataset.optionValue ?? '';

    productBlock.dataset.activeSku = sku;
    productBlock.dataset.activeColor = colorName;
    productBlock.dataset.activeColorName = optionName;
    productBlock.dataset.activeColorValue = optionValue;

    ProductBlockByColors.setActiveOption(productBlock, '[data-product-color]', option);
    ProductBlockByColors.updateImages(productBlock, sku);
    ProductBlockByColors.updateLinks(productBlock, colorName);
    ProductBlockByColors.syncFormOption(productBlock, optionName, optionValue);

    productBlock.dispatchEvent(new CustomEvent('vnda:sku-change', {
      detail: { sku },
    }));
  },

  handleOption: function (option) {
    const productBlock = option.closest('.product-block');
    if (!productBlock) return;

    const optionName = option.dataset.optionName ?? '';
    const optionValue = option.dataset.optionValue ?? '';

    productBlock.dataset.activeSizeName = optionName;
    productBlock.dataset.activeSizeValue = optionValue;

    ProductBlockByColors.setActiveOption(productBlock, '[data-product-option]', option);
    ProductBlockByColors.syncFormOption(productBlock, optionName, optionValue);
  },

  setInitialLinks: function () {
    document.querySelectorAll('.product-block[data-active-color]').forEach((productBlock) => {
      ProductBlockByColors.updateLinks(productBlock, productBlock.dataset.activeColor);
    });
  },

  init: function () {
    ProductBlockByColors.setInitialLinks();

    if (document.documentElement.hasAttribute('data-product-colors-ready')) return;

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;

      const color = event.target.closest('[data-product-color]');
      if (color) {
        event.preventDefault();
        ProductBlockByColors.handleColor(color);
        return;
      }

      const option = event.target.closest('[data-product-option]');
      if (!option) return;

      event.preventDefault();
      ProductBlockByColors.handleOption(option);
    });

    document.documentElement.setAttribute('data-product-colors-ready', 'true');
  },
};

window.ProductBlockByColors = ProductBlockByColors;

export default ProductBlockByColors;
